import React, { useContext, useEffect, useState, useRef } from 'react';
import '../astute-topic/astute-topic.scss';
import { useSupportCommonContent } from '../astute-topic/hook/use-support-common';
import { BRAND_ATTRIBUTES, SUPPORT_HUB_REGEX } from '../../support-constants';
import { useAnalytics } from '../../../hooks/use-analytics';
import ServerContext from '../../../contexts/serverContext';
import AstuteService, {
    serializeFaqSchemaJson,
} from '../../services/astute-service/astute-service';
import { useParams } from 'react-router';
import { useServerData } from '../../../hooks/use-server-data';
import DisclosuresLinkSyndicationAware from '../../../components/sections/disclosures-link/disclosures-link-syndication-aware';
import { useBbtContent } from '../browse-by-topic/hook/use-bbt-content';
import siteMapService from '../../services/site-map-service/site-map-service';
import xss from 'xss';
import { TopicPage } from '../../models/topic-page';
import EmplifiFeedback from '../EmplifiFeedback/emplifi-feedback';
import AstuteAccordion, {
    accordionQuery,
} from '../astute-accordion/astute-accordion';
import {
    fireOwnerArticleEvent,
    onAnchorClick,
    printButton,
    useAccordionHashNavigation,
} from '../astute-topic/astute-topic';
import BreadcrumbsAndViewBanner from '../breadcrumbs-and-view-banner/breadcrumbs-and-view-banner';
import WebFlashMessage from '../../../components/common/web-flash-message/web-flash-message';
import {
    disableLanguageLinksforCA,
    disableLanguageLinksforTH,
} from '../../../components/utils/syndicate-util/syndicate-util';
import { DiscoverabilityPageLoad } from '../../../services/shortcode-service/shortcode-service';
import { FusionTradeInButtonContainer } from '../../../components/common/tradeIn-button/fusion-tradeIn-button-container';
import { BrandUtil } from '../../../components/utils/brand-util/brand-util';
import {
    getCategoryNameFromTopicResponse,
    getCurrentLanguageRegionCodeFromServerContext,
    getCurrentRootFromServerContext,
    getSubCategoryNameFromTopicResponse,
    getTopicLabelFromTopicResponse,
} from '../astute-topic/util/astute-topic-util';
import { AstuteSearchBreadcrumb } from './astute-search-breadcrumb';

interface SearchParams {
    searchterm: string;
}

const AstuteSearch = () => {
    const { searchterm } = useParams<SearchParams>();
    const supportCommonContent = useSupportCommonContent();
    const serverContext = useContext(ServerContext);
    serverContext.noIndex = true;
    const [accordions, setAccordions] = useState<Element[] | null>();
    const [astuteTopicDiv, setAstuteTopicDiv] = useState<Element | null>();
    const bbtContent = useBbtContent();
    const languageCode = getCurrentLanguageRegionCodeFromServerContext(
        serverContext
    );
    const currentRoot = getCurrentRootFromServerContext(serverContext);
    const [fireEvents] = useAnalytics();
    const slugifiedSearchTerm = xss(
        searchterm && searchterm.replace(SUPPORT_HUB_REGEX.HYPHENS, ' '),
        {
            whiteList: {}, // empty, means filter out all tags
            stripIgnoreTag: true, // filter out all HTML not in the whitelist
            stripIgnoreTagBody: ['script'], // the script tag is a special case, we need
            // to filter out its content
        }
    );

    const fetchingRef = useRef<Promise<TopicPage> | null>(null);

    const astuteSearchResult = async () => {
        if (fetchingRef.current) {
            return fetchingRef.current;
        }

        let topicPage: TopicPage = {
            topicId: 0,
            metaDescription: '',
            hrefLangs: '',
            sessionId: '',
        };

        const fetchPromise = (async () => {
            try {
                topicPage = await new AstuteService().getSearchResults(
                    slugifiedSearchTerm,
                    serverContext.currentLanguageRegionCode,
                    serverContext.domain,
                    serverContext.root,
                    serverContext.brand
                );
                serverContext.documentTitle =
                    topicPage.topicHeading && slugifiedSearchTerm;

                serverContext.metaDescription = topicPage.metaDescription;
                serverContext.canonical = topicPage.canonical;
                return topicPage;
            } catch (error) {
                topicPage.error = `Search Page Error: ${error}`;
                return topicPage;
            } finally {
                fetchingRef.current = null;
            }
        })();
        fetchingRef.current = fetchPromise;
        return fetchPromise;
    };
    const searchResponse = useServerData('astuteSearch', astuteSearchResult)[0];
    const faqSchemaJson = searchResponse?.data?.faqSchema
        ? serializeFaqSchemaJson(searchResponse.data.faqSchema)
        : '';
    const astuteService = new AstuteService();
    const categoryName = astuteService.sanitizeString(
        getCategoryNameFromTopicResponse(searchResponse),
        languageCode
    );
    const subCategoryName = astuteService.sanitizeString(
        getSubCategoryNameFromTopicResponse(searchResponse),
        languageCode
    );
    const topicLabel = astuteService.sanitizeString(
        getTopicLabelFromTopicResponse(searchResponse),
        languageCode
    );
    const fireAdobeAnalytics = () => {
        const searchNonSearch = 'searched';
        fireEvents(
            ['search-nonsearch-click'],
            undefined,
            {
                searchNonSearch,
            },
            false
        );
        const discoverabilityPageLoad: DiscoverabilityPageLoad = {
            searchKeyword: slugifiedSearchTerm,
        };
        fireEvents(
            ['owner-search'],
            undefined,
            { discoverabilityPageLoad },
            false
        );
        fireOwnerArticleEvent(
            languageCode,
            categoryName?.replaceAll('-', ' '),
            subCategoryName?.replaceAll('-', ' '),
            topicLabel?.replaceAll('-', ' '),
            fireEvents,
            slugifiedSearchTerm
        );
    };

    useEffect(() => {
        fireAdobeAnalytics();
        disableLanguageLinksforTH();
        disableLanguageLinksforCA();
        document.addEventListener('click', onAnchorClick, true);
        if (searchResponse?.data?.error) {
            window.location.href = `${currentRoot}/`;
        }
        return () => {
            document.removeEventListener('click', onAnchorClick, true);
        };
    }, []);

    useEffect(() => {
        setAccordions(accordionQuery());

        const astuteTopicDiv = document.querySelector('.astute-response');
        setAstuteTopicDiv(astuteTopicDiv);
    }, [searchResponse, accordions?.length]);

    useAccordionHashNavigation(
        !!astuteTopicDiv && !!accordions?.length,
        searchResponse?.data?.bodyWoHeading
    );

    const categoryPath = siteMapService.getPathFromRouterConfigs(
        serverContext.currentLanguageRegionCode,
        serverContext.brand,
        'CategoryPageView',
        [categoryName || '']
    );

    const subCategoryPath = siteMapService.getPathFromRouterConfigs(
        serverContext.currentLanguageRegionCode,
        serverContext.brand,
        'SubCategoryPageView',
        [categoryName || '', subCategoryName || '']
    );

    const { isFord } = new BrandUtil();
    const langReg: string = serverContext.currentLanguageRegionCode || '';
    const isLincoln = serverContext.brand === 'lincoln';

    const isFocusTradeIn =
        searchResponse?.data?.topicId?.toString() == '1026505365' &&
        isFord &&
        langReg.toLowerCase() === 'en-us';

    return (
        <>
            {searchResponse && (
                <div className="astute-topic">
                    <WebFlashMessage
                        experience={'emplifi'}
                        fragmentName={'experience-notification-banner'}
                    />
                    <BreadcrumbsAndViewBanner
                        currentPage={slugifiedSearchTerm || ''}
                        hashId={'#bbt'}
                        previousCrumb={
                            !bbtContent?.hideBrowseByTopic &&
                            !(
                                searchResponse.data?.categoryId !== undefined &&
                                BRAND_ATTRIBUTES[
                                    serverContext.brand
                                ].excludeCatId.includes(
                                    searchResponse.data.categoryId
                                )
                            ) &&
                            searchResponse.data?.categoryName && (
                                <AstuteSearchBreadcrumb
                                    searchResponse={searchResponse}
                                    subCategoryPath={subCategoryPath}
                                    categoryPath={categoryPath}
                                    currentRoot={currentRoot}
                                />
                            )
                        }
                        searchTerm={slugifiedSearchTerm || ''}
                        searchClassName={'banner-margin sh-search'}
                    />
                    <div className="astute-topic-content">
                        {printButton(supportCommonContent?.printAriaLabel) ||
                            'click to print article'}

                        <div
                            className={`${
                                isLincoln ? 'astute-topic-content-wrapper' : ''
                            }`}
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html:
                                        '' +
                                        searchResponse.data?.topicHeading?.replace(
                                            /&nbsp;/g,
                                            ' '
                                        ),
                                }}
                            />

                            {faqSchemaJson && (
                                <script
                                    type="application/ld+json"
                                    data-testid="faq-schema-script"
                                    dangerouslySetInnerHTML={{
                                        __html: faqSchemaJson,
                                    }}
                                />
                            )}

                            {astuteTopicDiv &&
                                accordions &&
                                isFocusTradeIn &&
                                accordions?.length > 0 && (
                                    <FusionTradeInButtonContainer
                                        topicResponse={searchResponse}
                                        astuteTopicDiv={astuteTopicDiv}
                                        accordions={accordions}
                                        isFocusTradeIn={isFocusTradeIn}
                                    />
                                )}

                            {astuteTopicDiv &&
                                accordions &&
                                !isFocusTradeIn &&
                                accordions.length > 0 && (
                                    <AstuteAccordion
                                        topic={astuteTopicDiv.innerHTML}
                                        accordions={accordions}
                                        topicId={searchResponse.data?.topicId.toString()}
                                    />
                                )}
                            <div
                                className={`astute-response ${
                                    accordions && accordions.length > 0
                                        ? 'replace-accordion'
                                        : ''
                                }`}
                                dangerouslySetInnerHTML={
                                    searchResponse.data?.topicId &&
                                    searchResponse.data?.topicId > 0 &&
                                    !searchResponse.data.categoryId
                                        ? {
                                              __html:
                                                  '' +
                                                  supportCommonContent?.topicErrorMessage,
                                          }
                                        : {
                                              __html:
                                                  '' +
                                                  searchResponse.data
                                                      ?.bodyWoHeading,
                                          }
                                }
                            />
                            {searchResponse.data?.topicId &&
                            searchResponse.data.categoryId ? (
                                <EmplifiFeedback
                                    topicId={searchResponse.data?.topicId}
                                    sessionId={
                                        searchResponse.data?.sessionId || ''
                                    }
                                />
                            ) : (
                                <></>
                            )}
                            {searchResponse?.data?.relatedTopics && (
                                <div className="article-related-topics">
                                    <h2>
                                        {supportCommonContent?.relatedTopics}
                                    </h2>

                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                searchResponse.data
                                                    ?.relatedTopics,
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <DisclosuresLinkSyndicationAware />
                </div>
            )}
        </>
    );
};
export default AstuteSearch;
